import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { Download, Upload, AlertTriangle, CheckCircle, Loader2 } from "lucide-react";
import { isSupabaseConfigured, supabase } from "@/lib/supabase";

const CSV_HEADERS = ["title", "author", "content_type", "categories", "description_short", "description_long", "subtitle", "tags"];

const TEMPLATE_CSV = [
  CSV_HEADERS.join(","),
  '"El Progreso del Peregrino","John Bunyan","mixed","Devocional, Clásicos","Una alegoría cristiana clásica","","Versión completa","fe, clásico"',
  '"Guía de oración","Autor Ejemplo","pdf","Oración","Guía práctica para la oración diaria","","","oración, guía"',
].join("\n");

type ParsedRow = {
  title: string;
  author: string;
  content_type: string;
  categories: string[];
  description_short: string;
  description_long: string;
  subtitle: string;
  tags: string[];
  errors: string[];
};

function parseCSV(text: string): ParsedRow[] {
  // Strip UTF-8 BOM that Excel adds
  const clean = text.replace(/^\ufeff/, "");
  const lines = clean.split("\n").map((l) => l.trim()).filter(Boolean);
  if (lines.length < 2) return [];

  // Skip header
  const dataLines = lines.slice(1);
  return dataLines.map((line) => {
    // Parse CSV respecting quoted fields
    const fields: string[] = [];
    let current = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === "," && !inQuotes) {
        fields.push(current.trim());
        current = "";
      } else {
        current += char;
      }
    }
    fields.push(current.trim());

    const errors: string[] = [];
    const title = fields[0] || "";
    const author = fields[1] || "";
    const contentType = (fields[2] || "").toLowerCase();
    const categoriesRaw = fields[3] || "";
    const descShort = fields[4] || "";
    const descLong = fields[5] || "";
    const subtitle = fields[6] || "";
    const tagsRaw = fields[7] || "";

    if (!title) errors.push("Título vacío");
    if (!["audio", "pdf", "mixed"].includes(contentType)) errors.push(`Tipo "${contentType}" inválido`);
    if (!author) errors.push("Sin autor");

    return {
      title,
      author,
      content_type: contentType,
      categories: categoriesRaw.split(",").map((c) => c.trim()).filter(Boolean),
      description_short: descShort,
      description_long: descLong,
      subtitle,
      tags: tagsRaw.split(",").map((t) => t.trim()).filter(Boolean),
      errors,
    };
  });
}

function slugify(input: string): string {
  return input
    .toLowerCase()
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "");
}

export default function BulkImportTab() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [fileName, setFileName] = useState("");

  const validRows = rows.filter((r) => r.errors.length === 0);
  const errorRows = rows.filter((r) => r.errors.length > 0);

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      const parsed = parseCSV(text);
      // Detect duplicate titles within the CSV
      const titleCount = new Map<string, number>();
      for (const row of parsed) {
        const key = row.title.toLowerCase();
        titleCount.set(key, (titleCount.get(key) || 0) + 1);
      }
      for (const row of parsed) {
        if (row.title && (titleCount.get(row.title.toLowerCase()) || 0) > 1) {
          row.errors.push("Título duplicado en CSV");
        }
      }
      setRows(parsed);
    };
    reader.readAsText(file);
  };

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE_CSV], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "senda-import-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const importMutation = useMutation({
    mutationFn: async () => {
      if (!isSupabaseConfigured || !supabase) throw new Error("Supabase no configurado");

      // Get existing categories to map names → ids
      const { data: existingCats } = await supabase.from("categories").select("id,name");
      const catNameToId = new Map<string, string>();
      for (const cat of existingCats || []) catNameToId.set(cat.name.toLowerCase(), cat.id);

      // Create missing categories (normalize: capitalize first letter)
      const normalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
      const allCatNames = new Set(validRows.flatMap((r) => r.categories.map(normalize)));
      const missingCats = [...allCatNames].filter((name) => !catNameToId.has(name.toLowerCase()));
      if (missingCats.length > 0) {
        const { data: newCats, error: catError } = await supabase
          .from("categories")
          .insert(missingCats.map((name) => ({ name })))
          .select("id,name");
        if (catError) throw catError;
        for (const cat of newCats || []) catNameToId.set(cat.name.toLowerCase(), cat.id);
      }

      const now = new Date().toISOString();
      let created = 0;

      for (const row of validRows) {
        const contentId = crypto.randomUUID().slice(0, 8);
        const slug = slugify(row.title) + "-" + contentId;

        // Insert content item
        const { error: contentError } = await supabase.from("content_items").insert({
          id: contentId,
          slug,
          title: row.title,
          subtitle: row.subtitle,
          author: row.author,
          category: row.categories[0] || "",
          content_type: row.content_type,
          status: "draft",
          is_featured: false,
          description_short: row.description_short,
          description_long: row.description_long,
          tags: row.tags,
          language: "es",
          cover_image_url: "",
          published_at: null,
          updated_at: now,
        });
        if (contentError) throw new Error(`Error en "${row.title}": ${contentError.message}`);

        // Insert access rule (free by default)
        await supabase.from("access_rules").insert({
          content_item_id: contentId,
          access_type: "free",
          plan_id: null,
          price: null,
        });

        // Insert category relationships
        const catIds = row.categories
          .map((name) => catNameToId.get(normalize(name).toLowerCase()))
          .filter(Boolean) as string[];
        if (catIds.length > 0) {
          await supabase.from("content_categories").insert(
            catIds.map((cid) => ({ content_item_id: contentId, category_id: cid }))
          );
        }

        created++;
      }

      return created;
    },
    onSuccess: (count) => {
      queryClient.invalidateQueries({ queryKey: ["content-list-data"] });
      queryClient.invalidateQueries({ queryKey: ["content-categories-map"] });
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-data"] });
      toast({ title: "Importación completada", description: `${count} contenido${count !== 1 ? "s" : ""} creado${count !== 1 ? "s" : ""} como borrador.` });
      setRows([]);
      setFileName("");
      if (fileRef.current) fileRef.current.value = "";
    },
    onError: (e: any) => {
      const msg = e instanceof Error ? e.message : "Error durante la importación";
      toast({ title: "Importación parcial", description: `${msg}. Algunos contenidos pueden haberse creado — revisa el catálogo.`, variant: "destructive" });
      queryClient.invalidateQueries({ queryKey: ["content-list-data"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-data"] });
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">
            Sube un CSV con los metadatos. Se crearán como borradores — después puedes subir portadas, PDFs y audios desde cada item.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={downloadTemplate}>
          <Download className="h-4 w-4 mr-2" />Descargar template
        </Button>
      </div>

      <div className="flex items-center gap-3">
        <input
          ref={fileRef}
          type="file"
          accept=".csv,text/csv"
          className="text-sm file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-primary file:text-primary-foreground file:font-medium file:cursor-pointer hover:file:bg-primary/90"
          onChange={onFileChange}
        />
        {fileName && <span className="text-sm text-muted-foreground">{fileName}</span>}
      </div>

      {rows.length > 0 && (
        <>
          <div className="flex items-center gap-4">
            <Badge variant="secondary" className="gap-1">
              <CheckCircle className="h-3 w-3" />{validRows.length} válido{validRows.length !== 1 ? "s" : ""}
            </Badge>
            {errorRows.length > 0 && (
              <Badge variant="destructive" className="gap-1">
                <AlertTriangle className="h-3 w-3" />{errorRows.length} con errores
              </Badge>
            )}
          </div>

          <div className="rounded-lg border border-border overflow-hidden max-h-[400px] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8">#</TableHead>
                  <TableHead>Título</TableHead>
                  <TableHead>Autor</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Categorías</TableHead>
                  <TableHead>Estado</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row, i) => (
                  <TableRow key={i} className={row.errors.length > 0 ? "bg-destructive/5" : ""}>
                    <TableCell className="text-xs text-muted-foreground">{i + 1}</TableCell>
                    <TableCell className="font-medium text-sm">{row.title || <span className="text-destructive italic">vacío</span>}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{row.author}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">{row.content_type || "?"}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1 flex-wrap">
                        {row.categories.map((c) => (
                          <Badge key={c} variant="secondary" className="text-xs">{c}</Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      {row.errors.length > 0 ? (
                        <span className="text-xs text-destructive">{row.errors.join(", ")}</span>
                      ) : (
                        <CheckCircle className="h-4 w-4 text-primary" />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center gap-3">
            <Button
              onClick={() => importMutation.mutate()}
              disabled={validRows.length === 0 || importMutation.isPending}
            >
              {importMutation.isPending ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Importando...</>
              ) : (
                <><Upload className="h-4 w-4 mr-2" />Importar {validRows.length} como borradores</>
              )}
            </Button>
            <Button variant="outline" onClick={() => { setRows([]); setFileName(""); if (fileRef.current) fileRef.current.value = ""; }}>
              Cancelar
            </Button>
          </div>
        </>
      )}

      <div className="bg-muted/50 rounded-lg p-4 space-y-2">
        <p className="text-sm font-medium">Formato del CSV</p>
        <p className="text-xs text-muted-foreground">Columnas: <code className="bg-muted px-1 rounded">title, author, content_type, categories, description_short, description_long, subtitle, tags</code></p>
        <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside">
          <li><strong>content_type</strong>: audio, pdf, o mixed</li>
          <li><strong>categories</strong>: separadas por coma dentro de comillas (ej. "Devocional, Clásicos")</li>
          <li><strong>tags</strong>: separados por coma dentro de comillas</li>
          <li>Las categorías nuevas se crean automáticamente</li>
          <li>Todo se importa como borrador — publica después de subir archivos</li>
        </ul>
      </div>
    </div>
  );
}
